// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import {
  Permission,
  type PermissionAction,
  type PermissionPath,
} from '@/authorization/types';
import { ReactNode } from 'react';

interface PolicyGateProps {
  /**
   * The resource path to check permission for (type-safe from PermissionPath)
   */
  path: PermissionPath;

  /**
   * The action to check permission for
   */
  action: PermissionAction;

  /**
   * Content to render when the user has permission
   */
  children: ReactNode;

  /**
   * Optional content to render when the user doesn't have permission
   */
  fallback?: ReactNode;

  /**
   * Whether to allow implicit permissions (inherited from parent paths).
   * Defaults to true.
   */
  allowImplicit?: boolean;
}

/**
 * A component that conditionally renders content based on user permissions.
 *
 * This component provides a declarative way to show/hide content based on
 * the current user's authorization policy for a specific resource and action.
 *
 * @example
 * ```tsx
 * <PolicyGate path="time.rate" action="read">
 *   <RateDisplay />
 * </PolicyGate>
 * ```
 *
 * @example
 * ```tsx
 * <PolicyGate
 *   path="time.rate"
 *   action="read"
 *   fallback={<div>You don't have permission to view rates</div>}
 * >
 *   <RateDisplay />
 * </PolicyGate>
 * ```
 *
 * @example
 * ```tsx
 * <PolicyGate path="time.rate" action="update" allowImplicit={false}>
 *   <EditRateButton />
 * </PolicyGate>
 * ```
 */
export function PolicyGate(props: PolicyGateProps) {
  const { hasPermission } = usePermissionChecker();
  const allowImplicit = props.allowImplicit ?? true;

  const permission = hasPermission(props.path, props.action);

  // Check if the user has permission
  const hasAccess = (() => {
    switch (permission) {
      case Permission.PermissionExplicitTrue:
        return true;
      case Permission.PermissionImplicitTrue:
        return allowImplicit;
      case Permission.PermissionExplicitFalse:
      case Permission.PermissionImplicitFalse:
      case Permission.PermissionNotFound:
        return false;
      default:
        return false;
    }
  })();

  if (hasAccess) {
    return <>{props.children}</>;
  }

  return props.fallback ? <>{props.fallback}</> : null;
}
